export interface Project {
  id: string;
  uuid: string;
  key: string;
  name: string;
  iconUrl: URL;
}

export interface IssueType {
  value: string;
  label: string;
  iconUrl: string;
}

export interface Assignee {
  value: string;
  label: string;
  iconUrl: string;
}

export enum SupportedJiraIssueFields {
  SPRINT = 'JiraSprintField',
  NUMBER_FIELD = 'JiraNumberField', // Story points
  RICH_TEXT = 'JiraRichTextField', // Rich text field
  SINGLE_USER_PICKER = 'JiraSingleSelectUserPickerField',
  EPIC = 'JiraEpicLinkField',
  PARENT = 'JiraParentIssueField',
  DATE = 'JiraDatePickerField',
  DATE_TIME = 'JiraDateTimePickerField',
  COMPONENTS = 'JiraComponentsField',
  TEXT_FIELD = 'JiraSingleLineTextField',
  LABELS = 'JiraLabelsField', // multi + creatable
  REQUEST_TYPE = 'JsmRequestTypeField',
  PRIORITY = 'JiraPriorityField',
  ISSUE_LINKS = 'JiraIssueLinkField', // async select multi + type of issue
  ORGANIZATIONS = 'JsmOrganizationField',
  TIME_TRACKING = 'JiraTimeTrackingField',
  ISSUE_TYPE = 'JiraIssueTypeField',
  SINGLE_SELECT = 'JiraSingleSelectField',
  CHECKBOXES = 'JiraCheckboxesField',
  JIRA_VERSIONS_PICKER = 'JiraMultipleVersionPickerField',
  RADIO_SELECT = 'JiraRadioSelectField',
  MULTIPLE_SELECT = 'JiraMultipleSelectField',
  ATLASSIAN_TEAM = 'JiraAtlassianTeamField',
}

export const supportedFields: string[] = Object.values(SupportedJiraIssueFields);

export interface IssueFieldOption {
  label: string;
  value: string | number;
  iconUrl?: string;
  info?: string | null;
  meta?: Record<string, string>;
  group?: string;
}

export interface IssueField {
  id: string;
  name: string;
  options: IssueFieldOption[];
  additionalOptions?: IssueFieldOption[];
  defaultOption: IssueFieldOption | undefined;
  type: string;
  searchUrl?: string | null;
  dataType: string;
  fieldConfig?: { isRequired: boolean; isEditable?: boolean } | null;
  required?: boolean;
  description?: string | null;
}

export enum JiraImportErrorCodes {
  // For all errors that occur after user hit the import button
  JIRA_IMPORT_FAILED,
  // For all generic/unaccounted for errors that occur after user try to search in any mode
  JIRA_SEARCH_FAILED,
  JIRA_NLP_SEARCH_FAILED_TO_GENERATE_JQL,
  JIRA_JQL_SEARCH_INVALID,
}

export interface JiraImportError extends Error {
  code: JiraImportErrorCodes;
  [JIRA_IMPORT_ERROR_SYMBOL]: true;
}

export type JiraIssueByKeyLookup =
  | {
      readonly status: 'found';
      readonly issue: {
        readonly key: string;
        readonly webUrl: string;
      };
    }
  | { readonly status: 'not-found' }
  | { readonly status: 'invalid-response' };

export type JiraIssueLinkRecord = {
  readonly id: string;
  readonly type: {
    readonly id: string;
    readonly name: string;
    readonly inward: string;
    readonly outward: string;
  };
  readonly inwardIssue?: { readonly key: string };
  readonly outwardIssue?: { readonly key: string };
};

export type JiraIssueLinksLookup =
  | { readonly status: 'found'; readonly links: readonly JiraIssueLinkRecord[] }
  | { readonly status: 'invalid-response' };

export type JiraIssueLinkByIdLookup =
  | { readonly status: 'found'; readonly link: JiraIssueLinkRecord }
  | { readonly status: 'not-found' }
  | { readonly status: 'invalid-response' };

export const JIRA_IMPORT_ERROR_SYMBOL: unique symbol = Symbol('JIRA_IMPORT_ERROR');

export function isJiraImportError(error: unknown): error is JiraImportError {
  return (
    !!error &&
    typeof error === 'object' &&
    JIRA_IMPORT_ERROR_SYMBOL in (error as Record<string, unknown>)
  );
}
